import React from "react";
import { Alert, Autocomplete, Box, Button, Chip, FormControlLabel, IconButton, LinearProgress, Switch, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { subscribePoll } from "../../Layout/Poller";
import { PageContainer, SectionHeader, SelectorBar, SelectField, FormDialog, DataTable } from "../../Layout/Common";
import CountryPicker, { countryName } from "../../Layout/CountryPicker";
import { ALL, ID_RE, FIXED, ellipsis, mono, canEdit, canReadVersioning, asDate, utc, utcFull, toUtcInput, fromUtcInput, span,
         slotStatus, SlotChip, sortSlots, emptySlot, verLabel, verRange, clientPatchesOf, parseClient, slotVersionOptions, PlatformPicker,
         countryOptions } from "./liveops";

// LiveOps → Modes (Evil Tower's game-mode windows, the backend group `livemodes`; menus round 2026-10-23, Docs/Modes.md): the main
// menu's CHALLENGE icon shows while a challenge slot is live (counting down to its end), the PVP icon is permanent and takes the badge
// of a pvp slot. No definitions: a slot IS the schedule - a kind (challenge | pvp), a key (the mode definition id), the card's title,
// a UTC window, the badge and the targeting (platform, client version, country). Players receive the slots live or starting within
// 7 days (domain 16, wire 0.30) at their next session sync; every write bumps the branch's modes stamp. View needs `#liveops`, edits
// `liveops`. Deep link: /<game>/liveops/modes[/<slotId>][/<branch> when not Development].
const livemodes = (description, parts, options) => api.group("livemodes")(description, parts, options);
const REFRESH_MS = 60000;
const KINDS = [{ value: "challenge", label: "Challenge" }, { value: "pvp", label: "PvP" }];
const kindLabel = (k) => KINDS.find(x => x.value === k)?.label ?? k;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
// A new slot: from the next full UTC hour; a challenge window lasts 7 days, a pvp slot a year (it is "permanent": renew it)
const emptyMode = (kind = "challenge") => {
    const s = emptySlot("");
    const start = asDate(s.start);
    return { ...s, kind, title: "", badge: false, end: new Date(start.getTime() + (kind === "pvp" ? 365 : 7) * 86400000).toISOString() };
};

export default class ModesPage extends React.Component {
    loadToken = 0;
    state = {
        branch: "Development", data: null, loading: false, error: null,
        versionList: [],       // the versioning list (every branch): the version selects
        filter: ALL,           // the kind filter
        slotDialog: null,      // { mode: create | update, slot, busy, error }
        confirm: null,         // { id, label, busy, error }
        preview: null,         // { platform, client, clientText, country, at, busy, error, result }
    };

    componentDidMount() {
        const rest = (globals.route?.deep ?? []).filter(Boolean);
        const branch = rest.find(d => globals.branches.includes(d)) ?? "Development";
        const openId = rest.find(d => !globals.branches.includes(d)) ?? null;
        // sequenced: two requests in the same millisecond trip the backend's per-device anti-replay
        this.setState({ branch }, () => { this.load(false, openId).finally(this.loadVersions); });
        this.unsubscribe = subscribePoll(() => this.load(true), REFRESH_MS);
    }

    componentWillUnmount() { this.unsubscribe?.(); }

    syncUrl = (id) => replaceDeep([id ?? "", this.state.branch === "Development" ? "" : this.state.branch]);

    // ---------- loads ----------
    load = (poll = false, openId = null) => {
        const { branch } = this.state;
        const token = ++this.loadToken;
        if (!poll) this.setState({ loading: true, error: null });
        return livemodes("Loading the mode windows", ["list", branch], { silent: true })
            .then(r => {
                if (token !== this.loadToken) return true;
                this.setState({ data: r && typeof r === "object" ? r : null, loading: false, error: null });
                const slot = openId && (r?.slots ?? []).find(s => s.slotId === openId);
                if (slot) this.openSlot(slot);
                return true;
            })
            .catch(err => {
                if (token === this.loadToken) this.setState(s => ({ loading: false, error: poll ? s.error : err.message }));
                return false;
            });
    }

    // The client versions of the version selects; without the role only "any" and typed versions
    loadVersions = () => {
        if (!canReadVersioning()) { this.setState({ versionList: [] }); return Promise.resolve(); }
        return api.versioning("Loading versions", ["list"], { silent: true })
            .then(list => this.setState({ versionList: Array.isArray(list) ? list : [] }))
            .catch(() => this.setState({ versionList: [] }));
    }

    changeBranch = (branch) => this.setState({ branch, data: null }, () => { this.syncUrl(); this.load(); });

    // ---------- slots ----------
    openNewSlot = () => {
        const kind = this.state.filter !== ALL ? this.state.filter : "challenge";
        this.setState({ slotDialog: { mode: "create", slot: emptyMode(kind), busy: false, error: null } });
    }

    openSlot = (s) => {
        this.setState({ slotDialog: { mode: "update", slot: { ...s, title: s.title ?? "", platforms: [...(s.platforms ?? [])], countries: [...(s.countries ?? [])], note: s.note ?? "" }, busy: false, error: null } });
        this.syncUrl(s.slotId);
    }

    closeSlot = () => { this.setState({ slotDialog: null }); this.syncUrl(); }

    setSlot = (patch) => this.setState(s => ({ slotDialog: s.slotDialog && { ...s.slotDialog, slot: { ...s.slotDialog.slot, ...patch }, error: null } }));

    saveSlot = () => {
        const { slotDialog: d, branch } = this.state;
        const s = d.slot;
        const start = asDate(s.start), end = asDate(s.end);
        const key = (s.key ?? "").trim(), title = (s.title ?? "").trim();
        const problem = !KINDS.some(k => k.value === s.kind) ? "Pick the kind."
            : !ID_RE.test(key) ? "The key is the mode definition id: 1-40 letters, digits, '_' or '-' (e.g. challenge-week-01)."
            : title.length > 80 ? "The title is longer than 80 characters."
            : !start || !end ? "Start and end are required."
            : end <= start ? "The end must be after the start."
            : s.minVersion && s.maxVersion && s.maxVersion < s.minVersion ? "The max version is below the min version." : null;
        if (problem) { this.setState({ slotDialog: { ...d, error: problem } }); return; }
        const payload = {
            kind: s.kind, key, title, start: start.toISOString(), end: end.toISOString(), platforms: s.platforms ?? [],
            minVersion: s.minVersion ?? 0, maxVersion: s.maxVersion ?? 0, countries: s.countries ?? [], badge: !!s.badge, enabled: !!s.enabled, note: (s.note ?? "").trim(),
        };
        const create = d.mode === "create";
        this.setState({ slotDialog: { ...d, busy: true, error: null } });
        livemodes(create ? `Scheduling ${key}` : "Saving the slot", create ? ["slot-create", branch] : ["slot-update", branch, s.slotId], { payload })
            .then(() => { this.setState({ slotDialog: null }); this.syncUrl(); this.load(); })
            .catch(err => this.setState(st => ({ slotDialog: st.slotDialog && { ...st.slotDialog, busy: false, error: err.message } })));
    }

    deleteSlot = () => {
        const { confirm: c, branch } = this.state;
        this.setState({ confirm: { ...c, busy: true, error: null } });
        livemodes("Deleting the slot", ["slot-delete", branch, c.id])
            .then(() => { this.setState({ confirm: null }); this.load(); })
            .catch(err => this.setState(s => ({ confirm: s.confirm && { ...s.confirm, busy: false, error: err.message } })));
    }

    versionOptions(kind, current) { return slotVersionOptions(this.state.versionList, this.state.branch, kind, current); }

    // ---------- preview ----------
    openPreview = () => {
        const versions = clientPatchesOf(this.state.versionList, this.state.branch);
        this.setState({ preview: { platform: globals.platformNames[0], client: versions[0]?.value ?? "", clientText: "", country: "", at: "", busy: false, error: null, result: null } });
    }

    previewCompact() {
        const { preview: p, branch } = this.state;
        if (!p) return null;
        return p.clientText.trim() ? parseClient(p.clientText, branch) : (p.client === "" ? null : p.client);
    }

    runPreview = () => {
        const { preview: p, branch } = this.state;
        const compact = this.previewCompact();
        if (compact == null) return;
        const at = fromUtcInput(p.at);
        const parts = ["preview", branch, p.platform, compact];
        // positional: a time without a country passes "-" (no country)
        if (p.country || at) parts.push(p.country || "-");
        if (at) parts.push(at);
        this.setState({ preview: { ...p, busy: true, error: null } });
        livemodes("Previewing", parts, { silent: true })
            .then(result => this.setState(s => ({ preview: s.preview && { ...s.preview, busy: false, result: { ...result, compact } } })))
            .catch(err => this.setState(s => ({ preview: s.preview && { ...s.preview, busy: false, error: err.message, result: null } })));
    }

    // ---------- render ----------
    renderSchedule(filter) {
        const { data, loading, branch } = this.state;
        const edit = canEdit();
        if (!data) return null;
        const now = Date.now();
        const rows = sortSlots((data.slots ?? []).filter(s => filter === ALL || s.kind === filter), now);
        if (rows.length === 0) {
            return <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{loading ? "Loading…" : `No ${filter === ALL ? "mode" : kindLabel(filter)} windows on ${branch}.${edit ? " Schedule one with New slot." : ""}`}</Typography>;
        }
        const columns = [
            { id: "kind", label: "Kind", width: 90, render: s => <Chip size="small" variant="outlined" color={s.kind === "pvp" ? "secondary" : "primary"} label={kindLabel(s.kind)} sx={{ height: 20 }} /> },
            { id: "key", label: "Key", width: 180, sx: ellipsis, render: s => <span title={s.key} style={mono}>{s.key}</span> },
            { id: "title", label: "Title", width: 170, sx: ellipsis, render: s => (s.title ? <span title={s.title}>{s.title}</span> : <Typography component="span" variant="body2" color="text.secondary">client default</Typography>) },
            { id: "window", label: "Window (UTC)", width: 270, render: s => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                    <SlotChip status={slotStatus(s, now)} />
                    <Tooltip title={`${utcFull(s.start)} → ${utcFull(s.end)}`}><span>{utc(s.start)} → {utc(s.end)}</span></Tooltip>
                </Box>
            )},
            { id: "badge", label: "Badge", width: 70, render: s => (s.badge ? <Chip size="small" color="warning" label="dot" sx={{ height: 20 }} /> : "—") },
            { id: "platforms", label: "Platforms", width: 140, sx: ellipsis, render: s => ((s.platforms ?? []).length
                ? <span title={s.platforms.join(", ")}>{s.platforms.map(p => <Chip key={p} size="small" label={p} sx={{ mr: 0.5, height: 20 }} />)}</span> : "all") },
            { id: "versions", label: "Versions", width: 140, sx: ellipsis, render: s => verRange(s.minVersion, s.maxVersion) },
            { id: "countries", label: "Countries", width: 120, sx: ellipsis, render: s => ((s.countries ?? []).length
                ? <span title={s.countries.map(c => `${countryName(c)} (${c})`).join(", ")}>{s.countries.join(", ")}</span> : "all") },
            { id: "note", label: "Note", width: 180, sx: ellipsis, render: s => <span title={s.note ?? ""}>{s.note ?? ""}</span> },
            { id: "updated", label: "Updated (UTC)", width: 130, render: s => <Tooltip title={s.updatedBy ?? ""}><span>{utc(s.updatedDate)}</span></Tooltip> },
            { id: "actions", label: "", width: 100, align: "right", render: s => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={edit ? "Edit" : "View"}><IconButton size="small" onClick={() => this.openSlot(s)}>{edit ? <EditIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></Tooltip>
                    {edit && <Tooltip title="Delete"><IconButton size="small" onClick={() => this.setState({ confirm: { id: s.slotId, label: `${kindLabel(s.kind)} ${s.key} ${utc(s.start)} → ${utc(s.end)}`, busy: false, error: null } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                </Box>
            )},
        ];
        return <DataTable sx={FIXED} columns={columns} rows={rows} getRowKey={s => s.slotId} />;
    }

    renderSlotDialog() {
        const { slotDialog: d, branch } = this.state;
        if (!d) return null;
        const s = d.slot, create = d.mode === "create", edit = canEdit();
        const start = asDate(s.start), end = asDate(s.end);
        const length = start && end && end > start ? `lasts ${span(end - start)}` : "typed in UTC";
        return (
            <FormDialog open title={create ? `New mode window · ${branch}` : `${edit ? "Edit " : ""}${kindLabel(s.kind)} · ${s.key} · ${branch}`} onClose={this.closeSlot} onConfirm={this.saveSlot}
                        confirmText={create ? "Schedule" : "Save"} hideConfirm={!edit} cancelText={edit ? "Cancel" : "Close"}
                        confirmDisabled={d.busy} cancelDisabled={d.busy} maxWidth="md" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {d.error && <Alert severity="error">{d.error}</Alert>}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                        <SelectField label="Kind" size="small" minWidth={150} value={s.kind} options={KINDS} disabled={!edit} onChange={v => this.setSlot({ kind: v })} />
                        <TextField label="Key (mode definition id)" size="small" sx={{ width: 260 }} value={s.key ?? ""} disabled={!edit}
                                   error={!!s.key && !ID_RE.test(s.key.trim())} helperText="e.g. challenge-week-01" onChange={e => this.setSlot({ key: e.target.value })} />
                        <TextField label="Title" size="small" sx={{ flex: 1, minWidth: 220 }} value={s.title ?? ""} disabled={!edit}
                                   helperText="The card's text; empty = the client's default" slotProps={{ htmlInput: { maxLength: 80 } }} onChange={e => this.setSlot({ title: e.target.value })} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
                        <TextField label="Start (UTC)" type="datetime-local" size="small" value={toUtcInput(s.start)} disabled={!edit} sx={{ width: 230 }}
                                   slotProps={{ inputLabel: { shrink: true } }} helperText={`now ${utcFull(new Date().toISOString())}`}
                                   onChange={e => this.setSlot({ start: fromUtcInput(e.target.value) })} />
                        <TextField label="End (UTC)" type="datetime-local" size="small" value={toUtcInput(s.end)} disabled={!edit} sx={{ width: 230 }}
                                   slotProps={{ inputLabel: { shrink: true } }} helperText={length}
                                   onChange={e => this.setSlot({ end: fromUtcInput(e.target.value) })} />
                        <FormControlLabel label="Enabled" disabled={!edit} sx={{ mt: 0.5 }}
                                          control={<Switch checked={!!s.enabled} onChange={e => this.setSlot({ enabled: e.target.checked })} />} />
                        <Tooltip title="The client puts a dot on the icon (e.g. a new season)">
                            <FormControlLabel label="Badge" disabled={!edit} sx={{ mt: 0.5 }}
                                              control={<Switch checked={!!s.badge} onChange={e => this.setSlot({ badge: e.target.checked })} />} />
                        </Tooltip>
                    </Box>
                    <PlatformPicker value={s.platforms} disabled={!edit} onChange={v => this.setSlot({ platforms: v })} />
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <SelectField label="Min version" size="small" minWidth={220} value={s.minVersion ?? 0} options={this.versionOptions("min", s.minVersion)} disabled={!edit}
                                     onChange={v => this.setSlot({ minVersion: v })} />
                        <SelectField label="Max version" size="small" minWidth={220} value={s.maxVersion ?? 0} options={this.versionOptions("max", s.maxVersion)} disabled={!edit}
                                     onChange={v => this.setSlot({ maxVersion: v })} />
                        {this.state.versionList.length === 0 && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>No version list here: only “any”.</Typography>}
                    </Box>
                    <CountryPicker value={s.countries ?? []} onChange={v => (edit ? this.setSlot({ countries: v }) : null)} />
                    <TextField label="Note" size="small" fullWidth value={s.note ?? ""} disabled={!edit} onChange={e => this.setSlot({ note: e.target.value })} />
                    <Typography variant="caption" color="text.secondary">
                        Clients from wire 0.30 on receive the windows live now or starting within 7 days at their next session sync. Two live
                        challenge windows: the client shows the one ending first.
                        {!create && ` Slot ${s.slotId} · updated ${utcFull(s.updatedDate)} by ${s.updatedBy || "?"}.`}
                    </Typography>
                </Box>
            </FormDialog>
        );
    }

    renderConfirmDialog() {
        const { confirm: c, branch } = this.state;
        if (!c) return null;
        return (
            <FormDialog open title="Delete this mode window?" onClose={() => this.setState({ confirm: null })}
                        confirmText="Delete" onConfirm={this.deleteSlot} confirmDisabled={c.busy} cancelDisabled={c.busy}>
                <Typography variant="body2">{c.label} on {branch}: players stop seeing it at their next sync.</Typography>
                {c.busy && <LinearProgress sx={{ mt: 2 }} />}
                {c.error && <Alert severity="error" sx={{ mt: 2 }}>{c.error}</Alert>}
            </FormDialog>
        );
    }

    renderPreviewDialog() {
        const { preview: p, branch, versionList } = this.state;
        if (!p) return null;
        const versions = clientPatchesOf(versionList, branch);
        const compact = this.previewCompact();
        const r = p.result, modes = r?.modes ?? [];
        const set = (field) => (value) => this.setState(s => ({ preview: { ...s.preview, [field]: value } }));
        return (
            <FormDialog open title={`Preview for a player · ${branch}`} onClose={() => this.setState({ preview: null })}
                        maxWidth={false} PaperProps={{ sx: { width: 980, maxWidth: "95vw" } }} cancelText="Close"
                        confirmText="Preview" onConfirm={this.runPreview} confirmDisabled={compact == null || p.busy}>
                <Typography variant="body2" color="text.secondary">
                    What domain 16 answers a player with these facts: the challenge / pvp windows live now or starting within 7 days, targeting
                    applied, in the client's order (live first, the earliest end first). Empty time = now.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mt: 2, flexWrap: "wrap" }}>
                    <SelectField label="Platform" size="small" minWidth={130} value={p.platform} options={globals.platformNames} onChange={set("platform")} />
                    {versions.length > 0 && <SelectField label="Client version" size="small" minWidth={150} value={p.client} options={versions}
                                                         onChange={set("client")} disabled={!!p.clientText.trim()} />}
                    <TextField size="small" label={versions.length ? "or type M.m.p" : "Client version (M.m.p)"} sx={{ width: 170 }}
                               value={p.clientText} onChange={e => set("clientText")(e.target.value)} error={!!p.clientText.trim() && compact == null} />
                    <Autocomplete size="small" options={countryOptions()} value={p.country || null} sx={{ width: 240 }}
                                  onChange={(e, v) => set("country")(v ?? "")} getOptionLabel={c => `${countryName(c)} (${c})`}
                                  renderInput={params => <TextField {...params} label="Country" placeholder="none" />} />
                    <TextField label="At (UTC)" type="datetime-local" size="small" value={p.at} sx={{ width: 220 }}
                               slotProps={{ inputLabel: { shrink: true } }} onChange={e => set("at")(e.target.value)} />
                </Box>
                {p.busy && <LinearProgress sx={{ mt: 2 }} />}
                {p.error && <Alert severity="error" sx={{ mt: 2 }}>{p.error}</Alert>}
                {r && <>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        At {utcFull(r.at)}, {p.platform} {verLabel(r.compact)}{p.country ? ` in ${countryName(p.country)}` : ""}: {plural(modes.length, "window")} · domain version {r.version}
                    </Typography>
                    {modes.length > 0 && (
                        <DataTable sx={{ ...FIXED, mt: 1 }} rows={modes} getRowKey={m => m.slotId} columns={[
                            { id: "status", label: "", width: 100, render: m => <SlotChip status={m.live ? "live" : "upcoming"} /> },
                            { id: "kind", label: "Kind", width: 90, render: m => kindLabel(m.kind) },
                            { id: "key", label: "Key", width: 200, sx: ellipsis, render: m => <span title={m.title ?? ""} style={mono}>{m.key}</span> },
                            { id: "window", label: "Window (UTC)", width: 230, render: m => `${utc(m.start)} → ${utc(m.end)}` },
                            { id: "badge", label: "Badge", width: 70, render: m => (m.badge ? "dot" : "—") },
                        ]} />
                    )}
                </>}
            </FormDialog>
        );
    }

    render() {
        const { branch, data, loading, error } = this.state;
        const edit = canEdit();
        const slots = data?.slots ?? [];
        const filter = this.state.filter;
        const now = Date.now();
        const count = (kind, st) => slots.filter(s => s.kind === kind && slotStatus(s, now) === st).length;
        return (
            <PageContainer maxWidth={1440}>
                <SectionHeader title="Modes">
                    {data && <Tooltip title="The branch's modes stamp: it grows on every write, and players re-read their mode windows when it moves">
                        <Chip size="small" label={`stamp ${data.stamp}`} />
                    </Tooltip>}
                    {!edit && <Chip size="small" label="read only" />}
                    <IconButton size="small" onClick={() => this.load()} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
                    <Button variant="outlined" size="small" onClick={this.openPreview}>Preview for a player</Button>
                    {edit && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openNewSlot}>New slot</Button>}
                </SectionHeader>
                <SelectorBar sx={{ alignItems: "center" }}>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches} onChange={this.changeBranch} />
                    <SelectField label="Kind" size="small" minWidth={160} value={filter} options={[{ value: ALL, label: "All kinds" }, ...KINDS]} onChange={f => this.setState({ filter: f })} />
                    {data && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto", textAlign: "right" }}>
                            Challenge: {count("challenge", "live")} live, {count("challenge", "upcoming")} upcoming · PvP: {count("pvp", "live")} live, {count("pvp", "upcoming")} upcoming
                            <br />Times are UTC. Players see a change at their next session sync (wire 0.30 on).
                        </Typography>
                    )}
                </SelectorBar>
                {loading && <LinearProgress sx={{ mb: 1 }} />}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {this.renderSchedule(filter)}
                {this.renderSlotDialog()}
                {this.renderConfirmDialog()}
                {this.renderPreviewDialog()}
            </PageContainer>
        );
    }
}
